import React from 'react';
import {
  MagnifyingGlassIcon,
  CaretUpIcon,
  CaretDownIcon,
  DownloadIcon,
  CaretUpDownIcon,
} from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { downloadCSV } from '@/utils/csvDownload';
import { useTableSearch } from './hooks/useTableSearch';
import { useTableSort } from './hooks/useTableSort';

export type SortDirection = 'asc' | 'desc';

export interface TableColumn<T> {
  id: keyof T;
  label: string;
  sortable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (value: any, row: T) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headerRender?: () => React.ReactNode;
  minWidth?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
  stopRowClick?: boolean; // If true, clicking this cell won't trigger row click
}

export interface SummaryRow {
  [key: string]: React.ReactNode;
}

export interface GentlyTableProps<T> {
  data: T[] | undefined;
  columns: TableColumn<T>[];
  searchPlaceholder?: string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  searchFields?: (keyof T)[];
  showSearch?: boolean;
  actions?: React.ReactNode;
  summaryRow?: SummaryRow;
  isLoading?: boolean;
  className?: string;
  showDownload?: boolean;
  downloadFilename?: string;
  showCount?: boolean; // Show current count of displayed items
  customCountDisplay?: React.ReactNode; // Custom count display component
  getRowClassName?: (row: T) => string; // Custom className for each row
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultCellRender?: (value: any, row: T) => React.ReactNode; // Default render for cells without custom render

  // Scrollable table options
  maxHeight?: number | string; // Max height for table body (e.g., 600 or "600px")
  stickyHeader?: boolean; // Whether to make the header sticky when scrolling

  // Advanced overrides for server-side features
  serverSideSearch?: {
    value: string;
    onChange: (value: string) => void;
  };
  serverSideSorting?: {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (columnId: string, direction: 'asc' | 'desc') => void;
  };
  disableClientSideSort?: boolean;
  downloadAllData?: T[]; // For paginated tables, pass all data here for download
  defaultSort?: {
    sortBy: keyof T;
    sortDirection: SortDirection;
  };
}

/**
 * A clean, shadcn-based table component for displaying tabular data.
 * Supports client-side search, sorting, and customizable columns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function GentlyTable<T extends Record<string, any>>({
  data,
  columns,
  searchPlaceholder = 'Search...',
  onRowClick,
  emptyMessage = 'No data found',
  searchFields = [],
  showSearch = true,
  actions,
  summaryRow,
  isLoading = false,
  className,
  showDownload = true,
  downloadFilename = 'table-data',
  showCount = false,
  customCountDisplay,
  getRowClassName,
  defaultCellRender,
  maxHeight,
  stickyHeader = false,
  serverSideSearch,
  serverSideSorting,
  disableClientSideSort = false,
  downloadAllData,
  defaultSort,
}: GentlyTableProps<T>) {
  // Use custom hooks for search and sort logic
  const { searchQuery, filteredData, handleSearchChange } = useTableSearch({
    data,
    searchFields,
    serverSideSearch,
  });

  const {
    sortedData: filteredAndSortedData,
    handleSort,
    getSortIcon,
  } = useTableSort({
    data: filteredData,
    serverSideSorting,
    disableClientSideSort,
    defaultSort,
  });

  const handleSortClick = (columnId: keyof T) => {
    const column = columns.find((col) => col.id === columnId);
    handleSort(columnId, column?.sortable);
  };

  const handleRowClick = (row: T) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  const renderSortIcon = (columnId: keyof T, sortable?: boolean) => {
    const direction = getSortIcon(columnId);

    // If column is actively sorted, show directional arrow
    if (direction) {
      return direction === 'asc' ? (
        <CaretUpIcon className="ml-1 h-4 w-4" />
      ) : (
        <CaretDownIcon className="ml-1 h-4 w-4" />
      );
    }

    // If column is sortable but not sorted, show subtle bidirectional arrow
    if (sortable) {
      return (
        <CaretUpDownIcon className="ml-1 h-3 w-3 text-muted-foreground/40" />
      );
    }

    return null;
  };

  const handleDownload = () => {
    // Use downloadAllData if provided (for paginated tables), otherwise use filtered data
    const dataToDownload = downloadAllData || filteredAndSortedData;

    if (!dataToDownload || dataToDownload.length === 0) {
      return;
    }

    downloadCSV({
      data: dataToDownload,
      filename: downloadFilename,
      columns: columns.map((col) => ({
        id: String(col.id),
        label: col.label,
      })),
    });
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search bar and actions */}
      {(showSearch || actions || showDownload || showCount) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="relative max-w-sm">
                <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-8"
                />
              </div>
            )}
            {showCount &&
              !isLoading &&
              filteredAndSortedData &&
              (customCountDisplay || (
                <div className="text-sm text-muted-foreground">
                  Showing {filteredAndSortedData.length}{' '}
                  {filteredAndSortedData.length === 1 ? 'item' : 'items'}
                </div>
              ))}
          </div>

          <div className="flex gap-2">
            {actions}
            {showDownload && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={
                  !filteredAndSortedData || filteredAndSortedData.length === 0
                }
                title="Download CSV"
              >
                <DownloadIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Table */}
      <div
        className="rounded-md border overflow-x-auto"
        style={
          maxHeight
            ? {
                maxHeight:
                  typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
                overflowY: 'auto',
              }
            : undefined
        }
      >
        <Table>
          <TableHeader
            className={
              stickyHeader ? 'sticky top-0 z-10 bg-background' : undefined
            }
          >
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={String(column.id)}
                  className={cn(
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.sortable && 'cursor-pointer select-none',
                    column.className,
                  )}
                  style={{ minWidth: column.minWidth }}
                  onClick={(e) => {
                    if (column.sortable && !column.headerRender) {
                      handleSortClick(column.id);
                    }
                  }}
                >
                  <div
                    className={cn(
                      'flex items-center',
                      column.align === 'center' && 'justify-center',
                      column.align === 'right' && 'justify-end',
                    )}
                    onClick={(e) => {
                      if (column.headerRender) {
                        e.stopPropagation();
                      }
                    }}
                  >
                    {column.headerRender ? (
                      column.headerRender()
                    ) : (
                      <>
                        <span className="font-medium text-sm">
                          {String(column.id) === 'index' ? '#' : column.label}
                        </span>
                        {renderSortIcon(column.id, column.sortable)}
                      </>
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="text-sm text-muted-foreground">
                      Loading...
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredAndSortedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="text-sm text-muted-foreground">
                      {searchQuery
                        ? `No results found matching "${searchQuery}"`
                        : emptyMessage}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredAndSortedData.map((row, index) => (
                  <TableRow
                    key={index}
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      'hover:bg-muted/50',
                      getRowClassName?.(row),
                    )}
                    onClick={() => handleRowClick(row)}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={String(column.id)}
                        className={cn(
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right',
                          column.className,
                        )}
                        onClick={
                          column.stopRowClick
                            ? (e) => e.stopPropagation()
                            : undefined
                        }
                      >
                        {String(column.id) === 'index' ? (
                          <span className="font-medium text-sm">
                            {index + 1}
                          </span>
                        ) : column.render ? (
                          column.render(row[column.id], row)
                        ) : defaultCellRender ? (
                          defaultCellRender(row[column.id], row)
                        ) : (
                          <span className="text-sm">
                            {row[column.id] || '-'}
                          </span>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Summary Row */}
                {summaryRow && filteredAndSortedData.length > 0 && (
                  <TableRow className="border-t-2 bg-muted/25 font-medium">
                    {columns.map((column) => (
                      <TableCell
                        key={String(column.id)}
                        className={cn(
                          'font-semibold',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right',
                          column.className,
                        )}
                      >
                        {summaryRow[String(column.id)] || ''}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default GentlyTable;
